// Rule-based "This cycle in review" engine. Pure computation, no AI, no network.
// Returns a plain-English headline plus a list of observational callouts.
// Copy stays inform-don't-instruct (we state what happened, never "you should").
import { billDueDateInMonth } from './payday'

const gbp = n => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0)
const gbp2 = n => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0)

const NUM_WORDS = ['zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten']
const numWord = n => NUM_WORDS[n] || String(n)

function humanList(parts) {
  if (parts.length <= 1) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

// Bills whose due date falls inside the selected cycle, with per-category totals.
function billsInCycle(bills, selectedCycle) {
  const result = { count: 0, total: 0, byCategory: {} }
  if (!selectedCycle) return result
  const start = new Date(selectedCycle.startIso)
  const end = new Date(selectedCycle.endIso)
  for (const bill of bills) {
    if (!bill.amount) continue
    for (let m = 0; m < 3; m++) {
      const probe = new Date(start.getFullYear(), start.getMonth() + m, 1)
      const dueDate = billDueDateInMonth(bill, probe.getFullYear(), probe.getMonth())
      if (dueDate && dueDate >= start && dueDate <= end) {
        result.count += 1
        result.total += bill.amount
        if (bill.category) result.byCategory[bill.category] = (result.byCategory[bill.category] || 0) + bill.amount
        break
      }
    }
  }
  return result
}

export function buildCycleReview(d) {
  const {
    totalBudgeted = 0, cycleSpent = 0, spendingPct = 0, cycleUsedPct = 0,
    budgets = [], categorySpends = {}, bills = [], selectedCycle = null,
    billsDueThisWeekCount = 0, quickSpendCount = 0, quickSpendTotal = 0,
    quickIncomeTotal = 0, stsValue = 0,
  } = d

  if (totalBudgeted <= 0) return null

  const callouts = []
  const bic = billsInCycle(bills, selectedCycle)

  // Headline: spend vs budget, plus pace against how far the period has run.
  const pace = spendingPct <= cycleUsedPct
    ? 'tracking just inside the line'
    : spendingPct <= cycleUsedPct + 10
      ? 'roughly in step with the period'
      : 'running ahead of the days gone'
  const headline = `You have spent ${gbp(cycleSpent)} of your ${gbp(totalBudgeted)} budget. That is ${spendingPct}% used with the period ${cycleUsedPct}% through, ${pace}.`

  // Over-budget categories (top 3 by overshoot), or a positive line if all held.
  const over = budgets
    .map(b => {
      const spent = (categorySpends[b.category] || 0) + (bic.byCategory[b.category] || 0)
      const pct = b.budgeted > 0 ? Math.round((spent / b.budgeted) * 100) : 0
      return { cat: b.category, over: spent - b.budgeted, pct }
    })
    .filter(x => x.over > 0)
    .sort((a, b) => b.over - a.over)

  if (over.length) {
    const parts = over.slice(0, 3).map(x => `${x.cat} at ${x.pct}% (${gbp2(x.over)} over)`)
    const lead = over.length === 1 ? 'One category went over budget' : `${numWord(over.length)} categories went over budget`
    const body = over.length > 3 ? `The biggest three were ${humanList(parts)}.` : `${humanList(parts)}.`
    callouts.push({ tone: 'warn', icon: '⚠️', text: `${lead}. ${body}` })
  } else if (budgets.length) {
    callouts.push({ tone: 'good', icon: '✅', text: 'Every budget category is within its limit this period. Nicely held.' })
  }

  // Bills.
  if (billsDueThisWeekCount > 0) {
    callouts.push({ tone: 'warn', icon: '📅', text: `${billsDueThisWeekCount} bill${billsDueThisWeekCount > 1 ? 's' : ''} due this week.` })
  } else if (bic.total > 0) {
    callouts.push({ tone: 'good', icon: '✅', text: `Bills for this period are covered: ${gbp(bic.total)} across ${bic.count} bill${bic.count > 1 ? 's' : ''}, none due this week.` })
  }

  // Quick activity.
  if (quickSpendCount > 0) {
    let text = `${quickSpendCount} quick spend${quickSpendCount > 1 ? 's' : ''} logged (${gbp(quickSpendTotal)})`
    if (quickIncomeTotal > 0) text += `, against ${gbp(quickIncomeTotal)} of quick income`
    callouts.push({ tone: 'neutral', icon: '⚡', text: `${text}.` })
  }

  // Safe to Spend.
  if (stsValue < 0) {
    callouts.push({ tone: 'warn', icon: '🔻', text: `Safe to Spend sits at ${gbp(stsValue)}. Budgeted outgoings currently exceed money in, so we flag this period as over-committed.` })
  } else {
    callouts.push({ tone: 'good', icon: '🟢', text: `Safe to Spend is ${gbp(stsValue)} for the rest of the period.` })
  }

  return { headline, callouts: callouts.slice(0, 5) }
}
